import { all, delay, put, select, take, takeEvery, takeLatest } from '@redux-saga/core/effects';
import { AnalyticsTarget } from 'enums/AnalyticsTarget';
import { ChatViewMode } from 'enums/ChatViewMode';
import { MessageType } from 'enums/MessageType';
import { SmartButtonActionType } from 'enums/SmartButtonActionType';
import { StorageKey } from 'enums/StorageKey';
import { EHash } from 'enums/UrlHash';
import { WebViewAdapterEventSendable } from 'enums/WebViewAdapter';
import { WidgetEventName } from 'enums/WidgetEventName';
import { WidgetFeature } from 'enums/WidgetFeature';
import { WsAction } from 'enums/WsAction';
import { getWebviewAdapter } from 'hooks/useWebViewAdapter';
import { DefaultRootState } from 'react-redux';
import { call, debounce } from 'redux-saga/effects';
import { services } from 'services/container';
import { setCsiState } from 'state/csi/csi.actions';
import { selectFeatureEnabled } from 'state/features/features.selectors';
import { createReachGoalActionBy } from 'utils/analytics';
import { showBrowserNotification } from 'utils/notifications';
import { randomStr } from 'utils/random';

import { HistoryRequestType } from '../../enums/HistoryRequestType';
import {
    focusMessage,
    goToBottom,
    requestHistory,
    sendMessage,
    setFirstUnreadMsgId,
} from '../history/history.actions';
import {
    blinkMessage,
    ChatActionType,
    goToSearchResult,
    GoToSearchResultAction,
    SearchRequestAction,
    setAgentTyping,
    SetChatOpenAction,
    setChatViewMode,
    SetChatViewModeAction,
    setIsHoveringButtonsVisible,
    SetNotificationsEnabledAction,
    SetQuickActionsMenuAction,
    SetQuickActionsMenuReducer as setQuickActionsMenuReducer,
    setQuickActionsMenuVisible,
    SetQuotedMessageAction,
    setSearchResults,
    SetShowMsgTimeAndStatusAction,
    SetSoundNotificationsEnabledAction,
    SetUnreadCounterAction,
    SetVibrationEnabledAction,
    ShowToastAction,
    SmartButtonClickAction,
} from './chat.actions';

const typingAgentTimeout = 6; // Seconds

const updateCounterToHybridPlus = (state: DefaultRootState, unreadCounter: number) => {
    const webviewAdapter = getWebviewAdapter(state);

    if (
        !webviewAdapter ||
        webviewAdapter?.getCurrentPlatform?.() !== webviewAdapter?._platforms?.HYBRID_ANDROID
    )
        return;

    webviewAdapter.send(WebViewAdapterEventSendable.UpdateCounter, {
        count: unreadCounter,
    });
};

function* sendTypingSaga() {
    yield takeEvery(ChatActionType.SendTyping, function* () {
        services.vo.wsSend({
            action: WsAction.Typing,
        });
    });
}

function* receiveTypingSaga() {
    yield takeLatest(ChatActionType.ReceiveAgentTyping, function* () {
        yield put(setAgentTyping(true));
        yield delay(typingAgentTimeout * 1000);
        yield put(setAgentTyping(false));
    });
}

function* searchRequestSaga() {
    yield takeLatest(ChatActionType.SearchRequest, function* (action: SearchRequestAction) {
        const { query, aborter } = action.payload;

        if (!query || query.length < 3) {
            return;
        }

        const state: DefaultRootState = yield select();

        const is_fuzzy = selectFeatureEnabled(state, WidgetFeature.FuzzySearch);

        const res = yield services.vo
            .wsRequest(
                {
                    action: WsAction.SearchRequest,
                    is_fuzzy,
                    query,
                },
                undefined,
                undefined,
                aborter,
            )
            .catch(() => null);

        const results: string[] = (res?.results ?? [])
            .map((r) => r.id)
            .sort((id1, id2) => id1 - id2)
            .map((id) => String(id));

        services.analytics.sendAnalyticsAction(
            'Поиск',
            `Web-виджет. Получение${results.length === 0 ? ' пустых ' : ' '}результатов поиска`,
        );

        yield put(setSearchResults(results, is_fuzzy ? res?.words ?? [] : null));

        if (results?.length) {
            const focusResultIdx = results.length - 1;
            yield put(goToSearchResult(focusResultIdx));
        }
    });
}

function* goToSearchResultSaga() {
    yield debounce(
        300,
        ChatActionType.GoToSearchResult,
        function* (action: GoToSearchResultAction) {
            const resultIdx = action.payload;

            const state: DefaultRootState = yield select();
            const { searchResults } = state.chat;
            const { messages } = state.history;
            const msgId = searchResults?.[resultIdx];

            if (msgId) {
                if (!messages?.[msgId]) {
                    yield put(requestHistory(HistoryRequestType.OlderAndNewer, msgId));
                }

                yield put(focusMessage(msgId));
                yield put(blinkMessage(msgId));
            }
        },
    );
}

function* setQuotedMessageSaga() {
    yield takeLatest(ChatActionType.SetQuotedMessage, function* (action: SetQuotedMessageAction) {
        if (action.payload) {
            const viewMode: ChatViewMode = yield select(
                (state: DefaultRootState) => state.chat.viewMode,
            );

            if (viewMode !== ChatViewMode.Normal) {
                yield put(setChatViewMode(ChatViewMode.Normal));
            }
        }
    });
}

function* setViewModeSaga() {
    yield takeLatest(ChatActionType.SetViewMode, function* (action: SetChatViewModeAction) {
        const { goToMsgId, viewMode } = action.payload;

        if (viewMode !== ChatViewMode.Normal) return;

        const state: DefaultRootState = yield select();

        if (goToMsgId) {
            if (state.history.messages[goToMsgId]) {
                yield put(focusMessage(goToMsgId, false));
                yield put(blinkMessage(goToMsgId));
            } else {
                yield put(requestHistory(HistoryRequestType.OlderAndNewer, goToMsgId));
            }
        } else {
            yield put(goToBottom());
        }
    });
}

function* setNotificationsEnabledSaga() {
    yield takeLatest(
        ChatActionType.SetNotificationsEnabled,
        function* (action: SetNotificationsEnabledAction) {
            localStorage.setItem(StorageKey.NotificationsEnabled, JSON.stringify(action.payload));
        },
    );
}

function* setSoundNotificationsEnabledSaga() {
    yield takeLatest(
        ChatActionType.SetSoundNotificationsEnabled,
        function* (action: SetSoundNotificationsEnabledAction) {
            localStorage.setItem(
                StorageKey.SoundNotificationsEnabled,
                JSON.stringify(action.payload),
            );
        },
    );
}

function* setShowMsgTimeAndStatusSaga() {
    yield takeLatest(
        ChatActionType.SetShowMsgTimeAndStatus,
        function* (action: SetShowMsgTimeAndStatusAction) {
            localStorage.setItem(StorageKey.HideMsgTimeAndStatus, JSON.stringify(!action.payload));
        },
    );
}

function* setVibrationEnabledSaga() {
    yield takeLatest(
        ChatActionType.SetVibrationEnabled,
        function* (action: SetVibrationEnabledAction) {
            localStorage.setItem(StorageKey.VibrationEnabled, JSON.stringify(action.payload));
        },
    );
}

/**
 * TODO: Find a better way than repeatedly broadcasting the current value
 */
function* unreadCounterBroadcastSaga() {
    while (true) {
        const state: DefaultRootState = yield select();

        const unreadCounter = state.chat.unreadCounter;

        document.dispatchEvent(
            new CustomEvent(WidgetEventName.ChatUnreadCountUpdated, {
                detail: unreadCounter,
            }),
        );

        yield delay(1000);
    }
}

function* unreadCounterSaga() {
    while (true) {
        const prevUnreadCounter = yield select(
            (state: DefaultRootState) => state.chat.unreadCounter,
        );
        const action: SetUnreadCounterAction = yield take(ChatActionType.SetUnreadCounter);

        const notifEnabled: boolean = yield select(
            (state: DefaultRootState) =>
                selectFeatureEnabled(state, WidgetFeature.BrowserNotifications) &&
                state.chat.notificationsEnabled,
        );

        const unreadCounter = action.payload;
        const chatOpen: boolean = yield select((state: DefaultRootState) => state.chat.open);
        const chatVisible = chatOpen && document.visibilityState === 'visible';
        const authed: boolean = yield select((state: DefaultRootState) => state.connection.authed);

        document.dispatchEvent(
            new CustomEvent(WidgetEventName.ChatUnreadCountUpdated, {
                detail: unreadCounter,
            }),
        );

        updateCounterToHybridPlus(yield select((state) => state), unreadCounter);

        if (notifEnabled && !authed && !chatVisible && unreadCounter > prevUnreadCounter) {
            showBrowserNotification('ВТБ', 'Новое сообщение в чате с банком');
        }
    }
}

function* setChatOpenSaga() {
    yield takeEvery(ChatActionType.SetOpen, function* ({ payload: isOpen }: SetChatOpenAction) {
        document.dispatchEvent(
            new CustomEvent(WidgetEventName.ChatStateChanged, {
                detail: { isOpen },
            }),
        );

        const state: DefaultRootState = yield select();

        if (isOpen) {
            services.analytics.send(createReachGoalActionBy(AnalyticsTarget.CLICK_OPEN_CHAT));

            const { hasBeenRead, wasReceived } = state.csi;

            if (wasReceived && !hasBeenRead) {
                yield put(
                    setCsiState({
                        hasBeenRead: true,
                        wasReceived: true,
                    }),
                );
            }
        }

        if (!isOpen && state.history.firstUnreadMsgId) {
            yield put(setFirstUnreadMsgId(null));
        }
    });
}

function* smartButtonSaga() {
    yield takeEvery(
        ChatActionType.SmartButtonClick,
        function* ({ payload }: SmartButtonClickAction) {
            const { buttonAction, fallbackText } = payload;

            switch (buttonAction.type) {
                case SmartButtonActionType.Url: {
                    open(buttonAction.data, buttonAction?.target ?? '_blank');
                    break;
                }

                case SmartButtonActionType.Reply:
                default: {
                    yield put(
                        sendMessage({
                            type: MessageType.Text,
                            text: buttonAction.altText || buttonAction.data || fallbackText,
                            metadata: {
                                button: buttonAction,
                            },
                        }),
                    );
                    break;
                }
            }
        },
    );
}

function* showToastSaga() {
    yield takeEvery(ChatActionType.ShowToast, function* (action: ShowToastAction) {
        const { payload } = action;
        services.widget.eventChannel.emit('toast', false, {
            id: randomStr(),
            ...payload,
        });
    });
}

function* setQuickActionsMenuSaga() {
    yield takeLatest(
        ChatActionType.SetQuickActionsMenu,
        function* (action: SetQuickActionsMenuAction) {
            const { payload } = action;

            if (!payload) {
                yield put(setQuickActionsMenuVisible(false));
                yield put(setIsHoveringButtonsVisible(true));
                // yield delay(300);
            } else {
                yield put(setIsHoveringButtonsVisible(false));
                yield put(setQuickActionsMenuVisible(true));
            }
            // should be equal to @mbo-speed and @hovering-speed
            yield delay(400);
            yield put(setQuickActionsMenuReducer(payload));
        },
    );
}

// function* OpenContextMenuSaga() {
//     yield takeEvery(ChatActionType.OpenContextMenu, function* () {
//         window.location.hash = EHash.ContextMenu;
//     });
// }

function* CloseContextMenuSaga() {
    yield takeEvery(ChatActionType.CloseContextMenu, function* () {
        if (window.location.hash === `#${EHash.ContextMenu}`) {
            // TODO enchance and use navigate.ts
            yield call(
                () =>
                    new Promise((res) => {
                        history.back();
                        setTimeout(() => {
                            res(true);
                        }, 1000);
                    }),
            );
        }
    });
}

export function* chatSaga() {
    yield all([
        sendTypingSaga(),
        receiveTypingSaga(),
        searchRequestSaga(),
        goToSearchResultSaga(),
        setQuotedMessageSaga(),
        setViewModeSaga(),
        setNotificationsEnabledSaga(),
        setSoundNotificationsEnabledSaga(),
        setShowMsgTimeAndStatusSaga(),
        setVibrationEnabledSaga(),
        unreadCounterSaga(),
        unreadCounterBroadcastSaga(),
        setChatOpenSaga(),
        smartButtonSaga(),
        showToastSaga(),
        // OpenContextMenuSaga(),
        CloseContextMenuSaga(),
        setQuickActionsMenuSaga(),
    ]);
}
